import React, { useMemo } from 'react';

import { FormProps } from '@components/admin/FormDialog';
import { FormAlert } from '@components/Forms/FormAlert';
import { ManagementTextInput } from '@components/Forms/ManagementTextInput';
import { SubmittingOverlay } from '@components/Forms/SubmittingOverlay';
import { bookingInquiryStrings, bookingInquiryTradeConditionStrings } from '@data/bookingInquiry';
import { tradingAgencyOptions } from '@data/tradingAgency';
import { usePatchBookingInquiry } from '@hooks/mutation/dashboard/usePatchBookingInquiry';
import { AdminInquiryDetail, bookingInquiryForm, tradeCondtion } from '@models/BookingInquiry';
import { countryStrings } from '@models/Country';
import { makeMuiStyles, useCommonStyles } from '@styles';
import { createValidate, formStrings } from '@utils/forms';
import { Field, Form, Formik } from 'formik';
import { isLeft } from 'fp-ts/lib/Either';
import * as t from 'io-ts';

const useStyles = makeMuiStyles((theme, common) => ({
  container: {
    ...common.flexColumn,
    paddingBottom: 40,
  },
}));

const miscInfoSchema = t.type({
  trade_condition: bookingInquiryForm.props.trade_condition,
});

const validate = createValidate(miscInfoSchema);

export interface BookingInquiryShipperFormProps extends FormProps {
  adminInquiryDetail: AdminInquiryDetail;
}

export const BookingInquiryMiscInfoForm: React.FC<BookingInquiryShipperFormProps> = (props) => {
  const { formikRef, adminInquiryDetail } = props;

  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };

  const mutation = usePatchBookingInquiry(adminInquiryDetail.d2d_no);

  const initialValues = useMemo(
    () => ({
      trade_condition: String(adminInquiryDetail.trade_condition ?? ''),
      use_trading_agency: adminInquiryDetail.use_trading_agency,
      trading_agency_payer: adminInquiryDetail.trading_agency_payer,
      use_rcep: adminInquiryDetail.use_rcep,
    }),
    [adminInquiryDetail],
  );

  return (
    <Formik<typeof initialValues>
      innerRef={formikRef}
      initialValues={initialValues}
      validate={validate}
      onSubmit={async (values) => {
        const decoded = miscInfoSchema.decode(values);
        if (isLeft(decoded)) {
          throw new Error('Unexpected error in decoding miscInfoSchema. ');
        }

        await mutation.mutateAsync(decoded.right as any);
      }}
    >
      <Form>
        <SubmittingOverlay />
        <FormAlert mutation={mutation} />

        <div className={classes.container}>
          <ManagementTextInput
            name="trade_condition"
            label={bookingInquiryStrings.tradeCondition}
            choose
            options={[...tradeCondtion.types.values()].map((literal) => ({
              label: `${bookingInquiryTradeConditionStrings[literal.value]}  (${
                adminInquiryDetail.cargos.cargos_by_port
                  ? bookingInquiryStrings.noPickup
                  : bookingInquiryStrings.needsPickup
              })`,
              value: literal.value,
            }))}
          />

          <ManagementTextInput
            name="use_trading_agency"
            label={bookingInquiryStrings.useTradingAgency}
            choose
            options={tradingAgencyOptions}
          />

          <Field name="use_trading_agency">
            {({ field }: any) => (
              <ManagementTextInput
                name="trading_agency_payer"
                label={bookingInquiryStrings.tradingAgencyPayer}
                choose
                disabled={field.value !== 'USED'}
                options={[
                  {
                    value: 'cn',
                    label: countryStrings.cn,
                  },
                  {
                    value: 'jp',
                    label: countryStrings.jp,
                  },
                ]}
              />
            )}
          </Field>
          <ManagementTextInput
            name="use_rcep"
            label={bookingInquiryStrings.useRcep}
            choose
            options={[
              {
                value: true,
                label: formStrings.use,
              },
              {
                value: false,
                label: formStrings.notUse,
              },
            ]}
          />
        </div>
      </Form>
    </Formik>
  );
};
